import { useCallback, useRef, useState } from 'react'
import type { AssetListAsset } from './asset-list-card.types'

/**
 * The identity of the asset set on screen - what a selection is only valid against.
 *
 * Sorted, so the same assets in a different order are the same set; derived from the
 * rendered assets rather than from the host's narrowing inputs, because a filter that
 * changes nothing must not clear a valid selection and a brand change that happens to keep
 * the same labels must not preserve an invalid one.
 */
const assetSetKey = (assets: AssetListAsset[]) =>
	assets
		.map((asset) => asset.id)
		.sort()
		.join(',')

export interface AssetListSelection {
	/** Whether the card is selecting: the tiles are toggles rather than links. */
	isSelecting: boolean
	selectedAssetIds: ReadonlySet<string>
	/** The selected assets in the kit's listed order, one entry per asset. */
	selectedAssets: AssetListAsset[]
	startSelecting: () => void
	/** Leave the mode and drop the selection - the two are one action. */
	exitSelecting: () => void
	toggleSelection: (assetId: string) => void
	toggleGroupSelection: (assetIds: string[], select: boolean) => void
	/**
	 * True on the render where the asset set changed and the mode was dropped with it. The
	 * caller uses it to discard anything else scoped to the selection that just went away -
	 * a download report describes assets that are no longer listed.
	 */
	didAssetSetChange: boolean
}

/**
 * The kit's selection: which assets are picked, and whether the card is picking at all.
 *
 * Selection is keyed by asset id and held as ONE set for the whole card rather than one per
 * role, because an asset listed under two kit roles is one asset - it is selected once and
 * downloaded once, which a per-group model could not express.
 *
 * A change to the asset set on screen drops both the selection and the mode: a selected
 * asset that is no longer listed cannot be deselected, and the reader is now looking at a
 * different set of assets than the one they were picking from.
 */
export function useAssetListSelection(assets: AssetListAsset[]): AssetListSelection {
	const [selectedAssetIds, setSelectedAssetIds] = useState<ReadonlySet<string>>(new Set())
	/**
	 * Held rather than derived from the selection, so emptying the selection mid-pick does not
	 * throw the reader out of the mode they are still working in.
	 */
	const [isSelecting, setIsSelecting] = useState(false)

	// Reset during render, not in an effect: an effect would let one frame paint a selection
	// that belongs to assets no longer on screen.
	const renderedKey = assetSetKey(assets)
	const seenKey = useRef(renderedKey)
	const didAssetSetChange = seenKey.current !== renderedKey
	if (didAssetSetChange) {
		seenKey.current = renderedKey
		if (selectedAssetIds.size > 0) setSelectedAssetIds(new Set())
		if (isSelecting) setIsSelecting(false)
	}

	const toggleSelection = useCallback((assetId: string) => {
		setSelectedAssetIds((current) => {
			const next = new Set(current)
			if (!next.delete(assetId)) next.add(assetId)
			return next
		})
	}, [])

	// Checking a whole category IS a way into selecting, so it turns the mode on rather than
	// filling a selection the tiles do not yet show as selectable.
	const toggleGroupSelection = useCallback((assetIds: string[], select: boolean) => {
		if (select) setIsSelecting(true)
		setSelectedAssetIds((current) => {
			const next = new Set(current)
			for (const assetId of assetIds) {
				if (select) next.add(assetId)
				else next.delete(assetId)
			}
			return next
		})
	}, [])

	const startSelecting = useCallback(() => setIsSelecting(true), [])

	const exitSelecting = useCallback(() => {
		setIsSelecting(false)
		setSelectedAssetIds(new Set())
	}, [])

	// In the kit's listed order, and distinct: the asset itself is the unit, not its
	// appearances across roles.
	const selectedAssets = assets.filter((asset) => selectedAssetIds.has(asset.id))

	return {
		isSelecting,
		selectedAssetIds,
		selectedAssets,
		startSelecting,
		exitSelecting,
		toggleSelection,
		toggleGroupSelection,
		didAssetSetChange,
	}
}
